import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { BarChart as MuiBarChart } from "@mui/x-charts/BarChart";
import { LineChart as MuiLineChart } from "@mui/x-charts/LineChart";
import type { ChartsAxisData } from "@mui/x-charts";
import { DashboardWidget } from "./DashboardWidget";

export interface StackedBarChartWidgetProps {
  series: Array<{
    id: string;
    label: string;
    data: number[];
    color?: string;
  }>;
  dates: string[];
  height?: number;
  title?: string;
  hideLegend?: boolean;
}

export const StackedBarChartWidget: React.FC<StackedBarChartWidgetProps> = ({
  series,
  dates,
  height = 250,
  title = "Daily Active Users",
  hideLegend = false,
}) => {
  const theme = useTheme();

  const defaultColors = [
    theme.palette.primary.main,
    theme.palette.warning.main,
    "#EAECF0",
  ];

  // Separate bar series (Completed) from line series (Enrollments/Enrolments)
  const lineSeries = useMemo(() => {
    return series.filter((s) => s.label.toLowerCase().includes("completed"));
  }, [series]);

  const barSeries = useMemo(() => {
    return series.filter(
      (s) =>
        s.label.toLowerCase().includes("enrol") ||
        s.label.toLowerCase().includes("session"),
    );
  }, [series]);

  // Use disabled state if placeholder data or all values are zero
  // const isDisabled = isPlaceholder || allValuesZero;
  const isDisabled = false;

  const barChartSeries = barSeries.map((s, index) => ({
    ...s,
    color: isDisabled
      ? theme.palette.action.disabled
      : s.color || defaultColors[index % defaultColors.length],
  }));

  const lineChartSeries = lineSeries.map((s, index) => ({
    ...s,
    color: isDisabled
      ? theme.palette.action.disabled
      : s.color ||
        defaultColors[(lineSeries.length + index) % defaultColors.length],
  }));

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formattedDates = dates.map(formatDate);

  // Calculate max value across all series and add padding (20% extra space at top)
  const yAxisMax = useMemo(() => {
    if (series.length === 0) return undefined;

    const allValues = series.flatMap((s) => s.data);
    const maxValue = Math.max(...allValues);

    // If max is 0, return undefined to let chart auto-scale
    if (maxValue === 0) return 1;

    // Add 20% padding at the top, but ensure minimum of 1 extra unit
    const padding = Math.max(maxValue * 0.2, 1);
    return Math.ceil(maxValue + padding);
  }, [series]);

  const commonXAxis = [
    {
      scaleType: "band" as const,
      data: formattedDates,
      disableLine: true,
      disableTicks: true,
      categoryGapRatio: 0.65,
      labelStyle: {
        fill: "#475467",
      },
      tickLabelStyle: {
        fill: "#475467",
      },
    },
  ];

  const commonYAxis = [
    {
      max: yAxisMax,
      tickMinStep: 1,
      disableLine: true,
      disableTicks: true,
      labelStyle: {
        fill: "#475467",
      },
      tickLabelStyle: {
        fill: "#475467",
      },
    },
  ];

  const allValuesZero = useMemo(() => {
    const values = series.flatMap((s) => s.data);
    return values.length > 0 && values.every((v) => v === 0);
  }, [series]);

  // Reserve space at top for legend so it doesn't overlap the chart or get clipped
  const legendTopSpace = hideLegend ? 0 : 28;

  // On touch devices, MUI's hover tooltip can't distinguish a tap from a
  // scroll-drag, causing it to flicker. Replace it with a tap-to-pin tooltip
  // that's confined inside this widget so it can never render over the page header.
  const isTouchDevice = useMediaQuery("(pointer: coarse)");
  const widgetRef = useRef<HTMLDivElement>(null);
  const [pinnedIndex, setPinnedIndex] = useState<number | null>(null);

  useEffect(() => {
    setPinnedIndex(null);
  }, [series, dates]);

  useEffect(() => {
    if (pinnedIndex === null) return undefined;
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        widgetRef.current &&
        !widgetRef.current.contains(event.target as Node)
      ) {
        setPinnedIndex(null);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [pinnedIndex]);

  const handleAxisTap = (_event: MouseEvent, data: ChartsAxisData | null) => {
    if (!isTouchDevice) return;
    if (!data) {
      setPinnedIndex(null);
      return;
    }
    setPinnedIndex((prev) => (prev === data.dataIndex ? null : data.dataIndex));
  };

  const tooltipSlotProps = {
    tooltip: { trigger: isTouchDevice ? ("none" as const) : ("axis" as const) },
  };

  const pinnedTooltipSeries = [...barChartSeries, ...lineChartSeries];

  return (
    <DashboardWidget title={title}>
      <Box
        ref={widgetRef}
        sx={{
          width: "100%",
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          opacity: isDisabled ? 0.5 : 1,
          pointerEvents: isDisabled ? "none" : "auto",
          position: "relative",
        }}
      >
        {allValuesZero && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              color: theme.palette.text.secondary,
              fontSize: 14,
            }}
          >
            No data in this period
          </Box>
        )}
        {lineSeries.length > 0 && barSeries.length > 0 ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: height + legendTopSpace,
            }}
          >
            {/* Bar Chart - Background */}
            <Box
              sx={{
                position: "absolute",
                top: legendTopSpace,
                left: 0,
                width: "100%",
                height,
              }}
            >
              <MuiBarChart
                height={height}
                hideLegend={true}
                series={[
                  ...barChartSeries.map((s) => ({
                    data: s.data,
                    label: s.label,
                    id: s.id,
                    color: s.color,
                    stack: "stack",
                    labelMarkType: "circle" as const,
                  })),
                  // Add line series as bars with minimal height so they appear in tooltip
                  ...(lineChartSeries.length > 0
                    ? lineChartSeries.map((s) => {
                        const originalData = s.data;
                        return {
                          data: originalData.map(() => 0.001), // Minimal height for hoverability
                          label: s.label,
                          id: s.id,
                          color: s.color,
                          stack: "stack",
                          labelMarkType: "circle" as const,
                          valueFormatter: (
                            value: number | null,
                            context: any,
                          ) => {
                            // Show actual line value instead of 0.001
                            const dataIndex = context?.dataIndex;
                            if (
                              dataIndex !== null &&
                              dataIndex !== undefined &&
                              originalData[dataIndex] !== undefined
                            ) {
                              return `${originalData[dataIndex]}`;
                            }
                            return value !== null && value > 0.001
                              ? `${value}`
                              : "";
                          },
                        };
                      })
                    : []),
                ]}
                xAxis={commonXAxis}
                yAxis={commonYAxis}
                grid={{ horizontal: true }}
                borderRadius={8}
                onAxisClick={handleAxisTap}
                slotProps={tooltipSlotProps}
              />
            </Box>
            {/* Line Chart - Overlay */}
            <Box
              sx={{
                position: "absolute",
                top: legendTopSpace,
                left: 0,
                width: "100%",
                height,
                pointerEvents: "none",
                "& .MuiChartsAxis-root": {
                  display: "none !important",
                },
                "& .MuiChartsAxis-label": {
                  display: "none !important",
                },
                "& .MuiChartsAxis-tick": {
                  display: "none !important",
                },
                "& .MuiChartsAxis-line": {
                  display: "none !important",
                },
              }}
            >
              {!allValuesZero && (
                <MuiLineChart
                  height={height}
                  hideLegend={true}
                  series={lineChartSeries.map((s) => ({
                    data: s.data,
                    label: s.label,
                    id: s.id,
                    color: s.color,
                    curve: "monotoneX",
                  }))}
                  xAxis={[
                    {
                      ...commonXAxis[0],
                      disableLine: true,
                      disableTicks: true,
                    },
                  ]}
                  yAxis={[
                    {
                      ...commonYAxis[0],
                      disableLine: true,
                      disableTicks: true,
                    },
                  ]}
                />
              )}
            </Box>
            {/* Legend - Show combined legend */}
            {!hideLegend && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 8,
                  display: "flex",
                  gap: 2,
                  pointerEvents: "auto",
                  zIndex: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  padding: "4px 8px",
                  borderRadius: 1,
                }}
              >
                {[...barChartSeries, ...lineChartSeries].map((s) => (
                  <Box
                    key={s.id}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: s.color,
                      }}
                    />
                    <Box
                      component="span"
                      sx={{ fontSize: "0.875rem", color: "#475467" }}
                    >
                      {s.label}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <MuiBarChart
            height={height}
            hideLegend={hideLegend}
            series={barChartSeries.map((s) => ({
              data: s.data,
              label: s.label,
              id: s.id,
              color: s.color,
              stack: "stack",
              labelMarkType: "circle",
            }))}
            slotProps={{
              legend: {
                position: { vertical: "top", horizontal: "end" },
              },
              ...tooltipSlotProps,
            }}
            xAxis={commonXAxis}
            yAxis={commonYAxis}
            grid={{ horizontal: true }}
            borderRadius={8}
            onAxisClick={handleAxisTap}
          />
        )}
        {pinnedIndex !== null && (
          <Box
            sx={{
              position: "absolute",
              left: `${((pinnedIndex + 0.5) / formattedDates.length) * 100}%`,
              top: legendTopSpace,
              transform:
                pinnedIndex === 0
                  ? "translateX(0)"
                  : pinnedIndex === formattedDates.length - 1
                    ? "translateX(-100%)"
                    : "translateX(-50%)",
              zIndex: 2,
              pointerEvents: "none",
              backgroundColor: theme.palette.background.paper,
              boxShadow: 3,
              borderRadius: 1,
              p: 1,
              minWidth: 120,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
            >
              {formattedDates[pinnedIndex]}
            </Typography>
            {pinnedTooltipSeries.map((s) => (
              <Box
                key={s.id}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: s.color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" noWrap>
                  {s.label}: {s.data[pinnedIndex]}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </DashboardWidget>
  );
};
